import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Message, Reaction } from './types';
import { useUserStore } from '../../store/userStore';
import { CHAT_URL } from '../../constants';
import { useModernToast } from '../../components/ModernToast';
import env from '../../utils/getEnv';
interface ChatContextType {
  socket: Socket | null;
  isConnected: boolean;
  messages: Message[];
  activeRoom: string | null;
  rooms: Room[];
  setRooms: (rooms: Room[]) => void;
  users: User[];
  currentUserId: string | null;
  sendMessage: (content: string, roomId: string) => void;
  joinRoom: (roomId: string, name: string, type: 'direct' | 'group' | 'support', description: string, userIds: string[]) => void;
  createDirectChat: (userId: string) => void;
  joinDirectChat: (roomId: string) => void;
  totalUnread: number;
  markedUsers: User[];
  groupChats: Room[];
  supportChats: Room[];
  setActiveRoom: (roomId: string | null) => void;
}

interface Room {
  id: string;
  name: string;
  type: 'direct' | 'group' | 'support';
  description?: string;
  members: string[];
  unread?: boolean;
  other_user?: User;
  userIds?: string[] | null;
}

interface User {
  id: string;
  username?: string;
  email: string;
  avatar?: string;
}

const ChatContext = createContext<ChatContextType | null>(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

const supportIds = [1, 127, 4734, 4746]

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Disable chat in non-production environments
  if (env !== 'prod') {
    const disabledValue: ChatContextType = {
      socket: null,
      isConnected: false,
      messages: [],
      activeRoom: null,
      rooms: [],
      setRooms: () => {},
      users: [],
      currentUserId: null,
      sendMessage: () => {},
      joinRoom: () => {},
      createDirectChat: () => {},
      joinDirectChat: () => {},
      totalUnread: 0,
      markedUsers: [],
      groupChats: [],
      supportChats: [],
      setActiveRoom: () => {},
    };
    return <ChatContext.Provider value={disabledValue}>{children}</ChatContext.Provider>;
  }

  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [markedUsers, setMarkedUsers] = useState<User[]>([]);
  const [groupChats, setGroupChats] = useState<Room[]>([]);
  const [supportChats, setSupportChats] = useState<Room[]>([]);
  const groupChatsRef = useRef<Room[]>([]);
  groupChatsRef.current = groupChats;
  const socketRef = useRef<Socket | null>(null);
  const { user, isAuthenticated } = useUserStore();
  const toast = useModernToast();
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || !isAuthenticated || !token) return;

    const newSocket = io(CHAT_URL, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/',
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 30000,
      auth: { token }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
    console.log('new soccet', newSocket, CHAT_URL)
    // Add connection status logging
    newSocket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      // Request user list and existing rooms when connected
      newSocket.emit('users:list');
      newSocket.emit('rooms:list');
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      // Only show error if user is authenticated and it's not a timeout (timeouts will retry)
      // Timeout errors are handled by reconnection logic, so we don't need to show toast for every attempt
      if (user && isAuthenticated && error.message !== 'timeout') {
        toast.error('Connection Error', 'Failed to connect to chat service. Retrying...');
      }
    });

    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
      // Only show error if user is authenticated
      if (user && isAuthenticated) {
        toast.error('Error', error.response?.data?.message || 'Something went wrong with the chat service');
      }
    });


    // Add handler for current user info
    newSocket.on('user:info', (userId: string) => {
      setCurrentUserId(userId);
    });

    // Add handler for receiving existing rooms
    newSocket.on('rooms:list', (roomsList: Room[]) => {
      setRooms(roomsList);
      // If there are rooms, set the first one as active
      if (roomsList.length > 0) {
        setTotalUnread(roomsList.filter(room => room.unread).length);
      }
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      // Log disconnect reason for debugging
      if (reason === 'io server disconnect') {
        // Server disconnected the socket, will need to manually reconnect
        console.warn('Socket disconnected by server:', reason);
      } else if (reason === 'io client disconnect') {
        // Client manually disconnected
        console.log('Socket manually disconnected');
      } else {
        // Network error or other issues - will auto-reconnect
        console.log('Socket disconnected, will attempt to reconnect:', reason);
      }
    });

    // Handle reconnection attempts
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Reconnection attempt ${attemptNumber}...`);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`Reconnected after ${attemptNumber} attempts`);
      setIsConnected(true);
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('Failed to reconnect after all attempts');
      if (user && isAuthenticated) {
        toast.error('Connection Failed', 'Unable to connect to chat service. Please refresh the page.');
      }
    });

    newSocket.on('message:receive', (message: Message) => {
      setMessages(prev => [...prev, message]);
      // Refresh rooms list so any previously archived rooms that were
      // unarchived by the incoming message get populated in the UI
      newSocket.emit('rooms:list');
    });

    newSocket.on('message:reaction', ({ messageId, reaction }: { messageId: string; reaction: Reaction }) => {
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || [];
          // Check if user already reacted with this emoji
          const existingReactionIndex = reactions.findIndex(
            r => r.userId === reaction.userId && r.emoji === reaction.emoji
          );

          if (existingReactionIndex >= 0) {
            // Remove reaction if user already reacted with this emoji
            return {
              ...msg,
              reactions: reactions.filter((_, index) => index !== existingReactionIndex)
            };
          } else {
            // Add new reaction
            return {
              ...msg,
              reactions: [...reactions, reaction]
            };
          }
        }
        return msg;
      }));
    });

    newSocket.on('message:reaction:update', ({ messageId, reactions }) => {
      // Update your UI with the new reactions
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          return { ...msg, reactions };
        }
        return msg;
      }));
    });

    newSocket.on('room:created', (room: Room) => {
      setRooms(prev => [...prev, room]);
      // Automatically join the room after creation
      // Set this as the active room
      setActiveRoom(room.id);
    });

    newSocket.on('room:joined', ({ roomId }) => {
      setActiveRoom(roomId);
    });

    newSocket.on('room:unread', ({ unreadRoomsCount, unreadRoomIds }: { unreadRoomsCount: number, unreadRoomIds: string[] }) => {
      setTotalUnread(unreadRoomsCount);
      const unreadRooms = rooms.filter(room => unreadRoomIds.includes(room.id));
      const unreadUserIds = unreadRooms.map(room => room.members).flat();
      const unreadUsers = users.filter(listUser => unreadUserIds.includes(listUser.id));
      setMarkedUsers(unreadUsers);
      const tempGroupChats = [...groupChatsRef.current]
      tempGroupChats.forEach(groupChat => {
        if (unreadRoomIds.includes(groupChat.id)) {
          groupChat.unread = true;
        }
      });
      setGroupChats(tempGroupChats);
      //mark the room as unread
      setRooms(prev => prev.map(room => unreadRoomIds.includes(room.id) ? { ...room, unread: true } : room));
    });

    newSocket.on('room:messages', (messages: Message[]) => {
      setMessages(prev => {
        // Filter out any existing messages for this room
        const otherRoomMessages = prev.filter(msg => msg.chat_room_id !== messages[0]?.chat_room_id);
        // Add the new messages
        return [...otherRoomMessages, ...messages];
      });
    });

    newSocket.on('users:list', (userList: any) => {
      const users = userList.filter((listUser: User) => listUser.id !== user?.id);
      setUsers(users);
    });

    newSocket.on('group:read', (roomId: string) => {
      const tempGroupChats = [...groupChatsRef.current]
      tempGroupChats.forEach(groupChat => {
        if (groupChat.id === roomId) {
          groupChat.unread = false;
        }
      });
      setGroupChats(tempGroupChats);
    });

    return () => {
      newSocket.disconnect();
      if (socketRef.current === newSocket) {
        socketRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    if (rooms.length > 0 && users.length > 0) {
      const unreadRooms = rooms.filter(room => room.unread && room.type === 'direct');
      const unreadUserIds = unreadRooms.map(room => room.members).flat();
      const unreadUsers = users.filter(listUser => unreadUserIds.includes(listUser.id));
      setMarkedUsers(unreadUsers);
      const groupChats = rooms.filter(room => room.type === 'group');
      setGroupChats(groupChats);
      const supportRooms = rooms.filter(room => room.type === 'support');

      //if the current user is support change the names of the support rooms to be Groundskeepers - [username] using the username of the user not in the supportIds array
      if (user?.role === 'SUPPORT') {
        supportRooms.forEach(room => {
          const otherUserId = room.members.find(id => !supportIds.includes(id));
          const otherUser = users.find(user => user.id === otherUserId);
          room.name = `Groundskeeper - ${otherUser?.username}`;
        });
      }
      setSupportChats(supportRooms);
    }
  }, [rooms, users, totalUnread, activeRoom]);

  const sendMessage = (content: string, roomId: string) => {
    if (!socket || !roomId) return;
    socket.emit('message:send', {
      roomId,
      content,
      type: 'text'
    });
  };

  const joinRoom = (roomId: string, name: string, type: 'direct' | 'group' | 'support', description: string, userIds: string[]) => {
    if (!socket || userIds.length === 0) return;
    //if the user id of the other user is in the markedUsers, remove it
    if (markedUsers.some(markedUser => markedUser.id === userIds[0])) {
      setMarkedUsers(markedUsers.filter(markedUser => markedUser.id !== userIds[0]));
    }
    socket.emit('room:join', {
      roomId,
      name,
      type,
      description,
      userIds
    });
  };

  const joinDirectChat = (roomId: string) => {
    if (!socket) return;
    socket.emit('room:join', {
      roomId,
      type: 'direct'
    });
  };


  const createDirectChat = (userId: string) => {
    if (!socket) {
      return;
    }
    // Check if a direct chat already exists with this user
    const existingRoom = rooms.find(
      room =>
        room.type === 'direct' &&
        room.members.includes(userId)
    );
    if (existingRoom) {
      //if the userId is in the markedUsers, remove it
      if (markedUsers.some(markedUser => markedUser.id === userId)) {
        setMarkedUsers(markedUsers.filter(markedUser => markedUser.id !== userId));
      }
      joinRoom(existingRoom.id, existingRoom.name, existingRoom.type, existingRoom.description || '', existingRoom.members);
      return;
    }

    // Create a new direct chat
    const otherUser = users.find(user => user.id === userId);
    const roomName = otherUser?.username || otherUser?.email || 'Direct Chat';
    joinRoom('', roomName, 'direct', '', [userId]); // TODO: add description
  };

  const value = {
    socket,
    isConnected,
    messages,
    activeRoom,
    rooms,
    users,
    currentUserId,
    sendMessage,
    joinRoom,
    createDirectChat,
    joinDirectChat,
    totalUnread,
    markedUsers,
    groupChats,
    supportChats,
    setActiveRoom,
    setRooms
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}; 